/**
 * Global scroll lock manager with reference counting and automatic cleanup.
 * Prevents stacked modals or overlays from permanently freezing page scrolling
 * or breaking browser navigation.
 */

let lockCount = 0;

/**
 * Requests a scroll lock on <body> and <html>.
 * Returns an unlock function to be called on unmount / dismiss.
 */
export function lockScroll(): () => void {
  if (typeof document === "undefined") return () => {};

  lockCount++;
  if (lockCount === 1) {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.style.touchAction = "none";
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    unlockScroll();
  };
}

/**
 * Decrements the lock count and restores scrolling if no other locks are active.
 */
export function unlockScroll(): void {
  if (typeof document === "undefined") return;

  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) {
    forceUnlockScroll();
  }
}

/**
 * Unconditionally removes all scroll locks, position overrides, and third-party
 * modal remnants (such as Razorpay iframes/containers) from <body> and <html>.
 */
export function forceUnlockScroll(): void {
  if (typeof document === "undefined") return;

  lockCount = 0;

  const props = ["overflow", "position", "top", "left", "right", "bottom", "width", "height", "touch-action", "pointer-events"];
  for (const prop of props) {
    document.body.style.removeProperty(prop);
    document.documentElement.style.removeProperty(prop);
  }

  document.body.style.overflow = "";
  document.body.style.position = "";
  document.body.style.touchAction = "";
  document.documentElement.style.overflow = "";
  document.documentElement.style.position = "";
  document.documentElement.style.touchAction = "";

  // Remove modal/lock classes if injected by modals
  const cleanClasses = (el: HTMLElement) => {
    Array.from(el.classList).forEach((cls) => {
      if (cls.toLowerCase().includes("modal-open")) {
        el.classList.remove(cls);
      }
    });
  };
  cleanClasses(document.body);
  cleanClasses(document.documentElement);
}
